import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef
} from '@angular/material/dialog';
import {
  trigger,
  state,
  style,
  animate,
  transition
} from '@angular/animations';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';

export interface CreateDatasetDialogData {
  sqlQuery: string;
  schema?: string;
  databaseId: number;
  databaseName: string;
}

@Component({
  selector: 'app-create-dataset-dialog',
  template: `
    <h2 mat-dialog-title>Create Dataset</h2>
    <mat-dialog-content>
      <p>Enter name for dataset:</p>
      <mat-form-field appearance="fill" style="width: 100%">
        <mat-label>Name</mat-label>
        <input matInput [(ngModel)]="datasetName" />
      </mat-form-field>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-button color="primary" (click)="onSubmit()">Create</button>
    </mat-dialog-actions>
  `,
  standalone: true,
  imports: [
    MatDialogActions,
    MatLabel,
    MatFormField,
    MatDialogContent,
    FormsModule
  ]
})
export class CreateDatasetDialogComponent {
  datasetName: string = '';
  isSubmitting: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CreateDatasetDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateDatasetDialogData,
    private sanitizer: DomSanitizer
  ) {
    // Generate a suggested name for the dataset
    this.datasetName =
      'Dataset_' + new Date().toISOString().slice(0, 10).replace(/-/g, '');
  }

  /**
   * Close the dialog without creating a dataset
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * Close the dialog and return the dataset name
   */
  onSubmit(): void {
    if (this.datasetName.trim()) {
      this.isSubmitting = true;

      // Simulate a brief delay for better UX feedback (feels more real)
      setTimeout(() => {
        this.dialogRef.close({
          datasetName: this.datasetName
        });
      }, 800);
    }
  }

  /**
   * Apply basic syntax highlighting to SQL query
   * @param sql SQL query string
   * @returns HTML with syntax highlighting classes applied
   */
  formatSql(sql: string): SafeHtml {
    if (!sql) return this.sanitizer.bypassSecurityTrustHtml('');

    // Limit the SQL to 200 characters for preview
    const limitedSql = sql.length > 200 ? sql.substring(0, 200) + '...' : sql;

    // Escape HTML entities
    let escapedSql = limitedSql
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Apply syntax highlighting with regex patterns
    // Keywords
    escapedSql = escapedSql.replace(
      /\b(SELECT|FROM|WHERE|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AS|AND|OR|NOT|IN|LIKE|BETWEEN|IS|NULL|GROUP BY|ORDER BY|HAVING|LIMIT|OFFSET|UNION|ALL|DISTINCT|COUNT|SUM|AVG|MIN|MAX|CASE|WHEN|THEN|ELSE|END)\b/gi,
      '<span class="keyword">$1</span>'
    );

    // Functions
    escapedSql = escapedSql.replace(
      /\b([A-Za-z0-9_]+)(?=\s*\()/g,
      '<span class="function">$1</span>'
    );

    // Strings
    escapedSql = escapedSql.replace(
      /'([^']*)'/g,
      '<span class="string">\'$1\'</span>'
    );

    // Numbers
    escapedSql = escapedSql.replace(
      /\b(\d+)\b/g,
      '<span class="number">$1</span>'
    );

    // Operators
    escapedSql = escapedSql.replace(
      /([=<>!]=|[<>]|\+|\-|\*|\/|\%)/g,
      '<span class="operator">$1</span>'
    );

    return this.sanitizer.bypassSecurityTrustHtml(escapedSql);
  }
}
